import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Snackbar,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { loanService } from '../services/loanService';
import { useAppStore } from '../stores/useAppStore';
import { useDashboardStore } from '../stores/dashboardStore';
import { UserActivityList } from '../features/dashboard/UserActivityList';
import { CurrentLoans } from '../features/dashboard/CurrentLoans';
import { AnalyticsChart } from '../features/dashboard/AnalyticsChart';
import { PersonalizedRecommendations } from '../features/dashboard/PersonalizedRecommendations';
import { DashboardErrorBoundary } from '../features/dashboard/DashboardErrorBoundary';
import { logger } from '../utils/logger';

// import type { User } from '../types';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAppStore();
  const {
    dashboardData,
    userActivities,
    isLoading: dashboardLoading,
    error: dashboardError,
    fetchDashboardData,
    fetchUserActivities
  } = useDashboardStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showPasswordChangeAlert, setShowPasswordChangeAlert] = useState(false);

  const handleReturnBooks = async (loanIds: string[]) => {
    try {
      await loanService.returnBooks(loanIds);
      // Show toast success at parent level to persist across child remounts
      setSnackbarMessage(`✅ Đã trả thành công ${loanIds.length} cuốn sách và cập nhật kho sách`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Refresh dashboard data to reflect changes, pass user ID
      if (user?.id) {
        await fetchDashboardData(user.id);
      } else {
        await fetchDashboardData();
      }
    } catch (error) {
      setSnackbarMessage('❌ Có lỗi xảy ra khi trả sách. Vui lòng thử lại.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw error;
    }
  };

  useEffect(() => {
    // Check if user is already in store
    if (user) {
      setIsLoading(false);
      // Check if user needs to change password
      if (user.changePasswordRequired) {
        setShowPasswordChangeAlert(true);
      }
      // Fetch dashboard data when user is available, pass user ID
      fetchDashboardData(user.id);
      fetchUserActivities();
      return;
    }

    // Get user data from sessionStorage as fallback
    const userData = sessionStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // User will be set by store, just check if data is valid
        setIsLoading(false);
        // Fetch dashboard data with user ID
        fetchDashboardData(parsedUser.id);
        fetchUserActivities();
      } catch (err) {
        logger.error('Failed to parse user data:', err);
        setError('Không thể tải thông tin người dùng');
        setIsLoading(false);
      }
    } else {
      setError('Không tìm thấy thông tin người dùng');
      setIsLoading(false);
    }
  }, [user, fetchDashboardData, fetchUserActivities]);

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear server-side session
      await apiService.post('/api/auth/logout', {});
    } catch (err) {
      logger.error('Logout API call failed:', err);
    } finally {
      // Use store logout method
      logout();
      navigate('/login');
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Quay lại đăng nhập
        </Button>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Không tìm thấy thông tin người dùng
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Quay lại đăng nhập
        </Button>
      </Container>
    );
  }

  return (
    <DashboardErrorBoundary>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Bảng điều khiển
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Chào mừng bạn đến với hệ thống thư viện số
          </Typography>
        </Box>

        {/* Dashboard Error */}
        {dashboardError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {dashboardError}
          </Alert>
        )}

        {/* Dashboard Loading */}
        {dashboardLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {/* Dashboard Content */}
        {!dashboardLoading && dashboardData && (
          <Box>
            {/* Tabs for different dashboard sections */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Tổng quan" />
                <Tab label="Hoạt động gần đây" />
                <Tab label="Sách đang mượn" />
                <Tab label="Thống kê" />
                <Tab label="Gợi ý" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* User Information Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Thông tin cá nhân
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Họ và tên
                        </Typography>
                        <Typography variant="body1">
                          {user.fullName}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {user.email}
                        </Typography>
                      </Box>
                      {user.phoneNumber && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Số điện thoại
                          </Typography>
                          <Typography variant="body1">
                            {user.phoneNumber}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Vai trò
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {user.roles.map((role, index) => {
                            const getRoleLabel = (r: string) => {
                              switch (r.toLowerCase()) {
                                case 'admin':
                                  return 'Quản trị viên';
                                case 'approver':
                                  return 'BGH'; // Ban Giám Hiệu
                                case 'librarian':
                                  return 'Thủ thư';
                                case 'teacher':
                                  return 'Giáo viên';
                                case 'student':
                                  return 'Học sinh';
                                default:
                                  return r;
                              }
                            };
                            const getRoleColor = (r: string) => {
                              switch (r.toLowerCase()) {
                                case 'admin':
                                  return 'error';
                                case 'librarian':
                                  return 'primary';
                                case 'teacher':
                                  return 'success';
                                case 'student':
                                  return 'info';
                                default:
                                  return 'default';
                              }
                            };
                            const roleColor = getRoleColor(role);
                            const isApprover = role.toLowerCase() === 'approver';
                            return (
                              <Chip
                                key={index}
                                label={getRoleLabel(role)}
                                color={isApprover ? undefined : (roleColor as any)}
                                size="small"
                                sx={isApprover ? {
                                  mr: 1,
                                  mb: 1,
                                  backgroundColor: '#9c27b0', // Màu tím
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: '#7b1fa2',
                                  }
                                } : { mr: 1, mb: 1 }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Activity Summary Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Tóm tắt hoạt động
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                              {dashboardData?.recentActivity?.totalBooksBorrowed || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Sách đã mượn
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="secondary">
                              {dashboardData?.recentActivity?.totalResourcesAccessed || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Sách số đã truy cập
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="success.main">
                              {dashboardData?.recentActivity?.totalLessonPlansSubmitted || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Giáo án đã nộp
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box textAlign="center">
                            <Typography variant="h4" color="warning.main">
                              {dashboardData?.currentLoans?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Sách đang mượn
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === 1 && (
              <UserActivityList
                activities={userActivities}
                isLoading={dashboardLoading}
                error={dashboardError}
              />
            )}

            {activeTab === 2 && (
              <CurrentLoans
                loans={dashboardData?.currentLoans || []}
                onReturnBooks={handleReturnBooks}
              />
            )}

            {activeTab === 3 && dashboardData?.analytics && (
              <AnalyticsChart analytics={dashboardData.analytics} />
            )}

            {activeTab === 4 && (
              <PersonalizedRecommendations
                recommendedBooks={dashboardData?.recommendedBooks || []}
                recommendedResources={dashboardData?.recommendedResources || []}
              />
            )}
          </Box>
        )}

        {/* Global Snackbar for dashboard actions */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
            {snackbarMessage}
          </Alert>
        </Snackbar>

      </Container>
    </DashboardErrorBoundary>
  );
};

export default DashboardPage;
